import { queryOptions, useQuery } from '@tanstack/react-query';
import { api } from 'lib/request/api';
import type {
  SidekiqQueueAssignment,
  SidekiqQueueAssignmentsResponse,
} from './types';

/** The manifest only changes on a deploy or an override flip, so keep it fresh for a minute. */
const STALE_MS = 60_000;

/** Stable empty array so the table's render identity is steady while loading or errored. */
const NO_ROWS: SidekiqQueueAssignment[] = [];

/**
 * Query options for the Sidekiq job→queue manifest. `select` normalizes the nullable string
 * columns to '' (so TanStack, which decides global-search eligibility from the first row, treats
 * every column as searchable) and sorts by class for a deterministic order — done once per fetch
 * rather than per render.
 *
 * @return {object} TanStack `queryOptions` for the manifest read.
 */
export const sidekiqQueueAssignmentsQuery = () =>
  queryOptions<SidekiqQueueAssignmentsResponse, Error>({
    queryKey: ['admin', 'sidekiq-queue-assignments'],
    queryFn: ({ signal }) =>
      api
        .get('admin/sidekiq_queue_assignments', { signal })
        .json<SidekiqQueueAssignmentsResponse>(),
    staleTime: STALE_MS,
    select: (data) => ({
      ...data,
      results: [...data.results]
        .map((row) => ({
          ...row,
          queue: row.queue ?? '',
          bulkQueue: row.bulkQueue ?? '',
          note: row.note ?? '',
        }))
        .sort((a, b) => a.jobClass.localeCompare(b.jobClass)),
    }),
  });

/**
 * The Sidekiq job→queue manifest, with load/error state for the admin page.
 *
 * @return {object} The rows, summary stats, generated-at timestamp, and load/error state.
 */
export const useSidekiqQueueAssignments = () => {
  const { data, isLoading, error } = useQuery(sidekiqQueueAssignmentsQuery());

  return {
    rows: data?.results ?? NO_ROWS,
    stats: data?.stats ?? null,
    generatedAt: data?.generatedAt ?? null,
    isLoading,
    error: error ?? null,
  };
};
